import React, { useEffect, useMemo, useState } from "react";
import { HelpCircle, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const steps = [
  { route: "/motorista", selector: '[data-testid="driver-add-route-btn"]', title: "Comece por aqui", text: "Toque em Fazer fechamento para registrar o seu dia." },
  { route: "/motorista/fechamento", selector: '[data-tour="closing-date"]', title: "Data do fechamento", text: "Confirme a data. O sistema permite hoje ou dias anteriores." },
  { route: "/motorista/fechamento", selector: '[data-tour="closing-type"]', title: "Tipo de fechamento", text: "Escolha Normal ou Por viagem, conforme o trabalho realizado." },
  { route: "/motorista/fechamento", selector: '[data-tour="closing-region"]', title: "Região", text: "Informe se a lista realizada foi de Guarulhos ou São Paulo." },
  { route: "/motorista/fechamento", selector: '[data-tour="closing-main-data"]', title: "Dados da operação", text: "No modo normal, informe rota, entregues e retornos. No modo por viagem, adicione cada viagem separadamente." },
  { route: "/motorista/fechamento", selector: '[data-tour="closing-proof"]', title: "Comprovante", text: "Anexe o print ou a foto solicitada. Confira se a imagem está legível." },
  { route: "/motorista/fechamento", selector: '[data-tour="closing-extras"]', title: "Informações adicionais", text: "Preencha fora de lista, devoluções, OnLog, AR e coletas quando houver." },
  { route: "/motorista/fechamento", selector: '[data-tour="closing-fuel"]', title: "Abastecimento", text: "Se usou o cartão da D1, informe o valor, a data e anexe o comprovante." },
  { route: "/motorista/fechamento", selector: '[data-testid="closing-submit-button"]', title: "Revise e envie", text: "Confira todos os números antes de enviar o fechamento para conferência." },
  { route: "/motorista/fechamento", selector: '[data-testid="nav-driver-history"]', title: "Histórico", text: "Aqui você acompanha os fechamentos enviados e o status da conferência." },
  { route: "/motorista/fechamento", selector: '[data-testid="nav-driver-monthly"]', title: "Resumo mensal", text: "Consulte os seus totais do período de 28 a 27 e baixe o PDF." },
];

export default function DriverGuidedTour() {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const step = useMemo(() => steps[index], [index]);

  useEffect(() => {
    if (!active) return;
    if (location.pathname !== step.route) {
      navigate(step.route);
      return;
    }
    let timer;
    let attempts = 0;
    const findTarget = () => {
      const element = document.querySelector(step.selector);
      if (!element && attempts++ < 30) {
        timer = window.setTimeout(findTarget, 100);
        return;
      }
      if (!element) { setRect(null); return; }
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      timer = window.setTimeout(() => {
        const box = element.getBoundingClientRect();
        setRect({ top: box.top - 6, left: box.left - 6, width: box.width + 12, height: box.height + 12 });
      }, 350);
    };
    findTarget();
    const refresh = () => {
      const element = document.querySelector(step.selector);
      if (element) {
        const box = element.getBoundingClientRect();
        setRect({ top: box.top - 6, left: box.left - 6, width: box.width + 12, height: box.height + 12 });
      }
    };
    window.addEventListener("resize", refresh);
    window.addEventListener("scroll", refresh, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", refresh);
      window.removeEventListener("scroll", refresh, true);
    };
  }, [active, step, location.pathname, navigate]);

  function start() {
    setIndex(0);
    setRect(null);
    setActive(true);
  }

  function finish() {
    setActive(false);
    setRect(null);
    window.localStorage.setItem("d1_driver_tour_completed", "1");
  }

  return (
    <>
      {!active && (
        <button
          type="button"
          onClick={start}
          aria-label="Abrir passo a passo"
          title="Ajuda — passo a passo"
          className="fixed bottom-24 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full border border-blue-400/50 bg-blue-600 text-white shadow-[0_0_25px_rgba(37,99,235,0.55)] transition hover:scale-105 hover:bg-blue-500"
          data-testid="driver-help-tour-button"
        >
          <HelpCircle className="h-7 w-7" />
        </button>
      )}

      {active && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Passo a passo do sistema">
          <div className="absolute inset-0 bg-black/70" />
          {rect && (
            <div
              className="pointer-events-none fixed rounded-2xl border-2 border-blue-400 bg-blue-400/10 shadow-[0_0_0_9999px_rgba(0,0,0,0.08),0_0_35px_rgba(96,165,250,0.8)] transition-all duration-300"
              style={rect}
            />
          )}
          <div className="fixed inset-x-3 bottom-4 mx-auto max-w-md rounded-2xl border border-blue-400/40 bg-[#131B2B] p-5 text-slate-100 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-400">Passo {index + 1} de {steps.length}</p>
                <h2 className="mt-1 font-display text-xl font-bold text-white">{step.title}</h2>
              </div>
              <button type="button" onClick={finish} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white" aria-label="Fechar tutorial"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">{step.text}</p>
            <div className="mt-5 flex items-center justify-between gap-2">
              <button type="button" onClick={finish} className="px-2 py-2 text-xs font-semibold text-slate-500 hover:text-slate-300">Pular</button>
              <div className="flex gap-2">
                <button type="button" disabled={index === 0} onClick={() => { setRect(null); setIndex((v) => Math.max(0, v - 1)); }} className="inline-flex items-center gap-1 rounded-xl border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-300 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Voltar</button>
                <button type="button" onClick={() => { if (index === steps.length - 1) finish(); else { setRect(null); setIndex((v) => v + 1); } }} className="inline-flex items-center gap-1 rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-500">{index === steps.length - 1 ? "Concluir" : "Próximo"}{index < steps.length - 1 && <ChevronRight className="h-4 w-4" />}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
